import {
  createFileRoute,
  type LinkProps,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { Command, Search } from "lucide-react"
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"

import { AppHeader } from "@/components/app-header"
import { Button } from "@/components/button"
import { AppNavbar } from "@/components/NavBar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { allAdminNavEntries } from "@/lib/admin-nav"
import { useEffectiveIsAdmin } from "@/lib/admin-view-store"
import { requireAuthBeforeLoad } from "@/lib/auth-guard"

interface CommandItem {
  label: string
  detail: string
  to: LinkProps["to"]
}

export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    await requireAuthBeforeLoad({ context, location })
  },
  component: AuthedLayout,
  pendingComponent: () => (
    <div
      className="flex min-h-screen items-center justify-center"
      role="status"
    >
      <Spinner className="size-6" />
      <span className="sr-only">Loading session...</span>
    </div>
  ),
  pendingMs: 200,
  pendingMinMs: 300,
})

function AuthedLayout() {
  const { auth } = Route.useRouteContext()
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false)
  const [commandQuery, setCommandQuery] = useState("")
  const deferredCommandQuery = useDeferredValue(commandQuery)
  const navigate = useNavigate()
  const effectiveIsAdmin = useEffectiveIsAdmin(auth.user?.isAdmin ?? false)

  const commandItems = useMemo(() => {
    const items: CommandItem[] = [
      {
        label: "Home",
        detail: "Dashboard and quick links",
        to: "/",
      },
      {
        label: "Guild War Signup",
        detail: "Set availability and role",
        to: "/guild-war-signup",
      },
      {
        label: "Guild War Roster",
        detail: "View published assignments",
        to: "/guild-war-roster",
      },
      {
        label: "Boss Talents",
        detail: "Review talent submissions",
        to: "/boss-talents",
      },
      {
        label: "Absences",
        detail: "Log and view your absences",
        to: "/absences",
      },
      {
        label: "Theme Builder",
        detail: "Create, share, and import custom color themes",
        to: "/theme-builder",
      },
    ]

    if (effectiveIsAdmin) {
      items.push(
        ...allAdminNavEntries.map((entry) => ({
          label: `Admin: ${entry.label}`,
          detail: entry.description,
          to: entry.to,
        })),
      )
    }

    return items
  }, [effectiveIsAdmin])

  const filteredCommandItems = useMemo(() => {
    if (!isCommandPaletteOpen) {
      return commandItems
    }

    const normalizedQuery = deferredCommandQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return commandItems
    }

    return commandItems.filter((item) => {
      return (
        item.label.toLowerCase().includes(normalizedQuery) ||
        item.detail.toLowerCase().includes(normalizedQuery) ||
        item.to!.toLowerCase().includes(normalizedQuery)
      )
    })
  }, [commandItems, deferredCommandQuery, isCommandPaletteOpen])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isModifierPressed = event.metaKey || event.ctrlKey
      if (!isModifierPressed || event.key.toLowerCase() !== "k") {
        return
      }

      event.preventDefault()
      setIsCommandPaletteOpen((current) => !current)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  const redirectingRef = useRef(false)

  useEffect(() => {
    if (!auth.user && !redirectingRef.current) {
      redirectingRef.current = true
      const redirect =
        typeof window === "undefined"
          ? "/"
          : `${window.location.pathname}${window.location.search}${window.location.hash}`

      void navigate({
        to: "/login",
        search: {
          redirect,
        },
      })
    }
  }, [auth.user, navigate])

  if (!auth.user) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        role="status"
      >
        <Spinner className="size-6" />
        <span className="sr-only">Redirecting to login...</span>
      </div>
    )
  }

  return (
    <div className="layout-shell flex min-h-screen w-full flex-col">
      <AppHeader
        user={auth.user}
        mobileNavOpen={isMobileNavOpen}
        onMobileNavOpenChange={setIsMobileNavOpen}
        mobileNavContent={<AppNavbar user={auth.user} />}
      />

      <div className="grid min-h-0 flex-1 gap-5 px-2 py-3 md:px-3 md:py-4 lg:grid-cols-[clamp(14rem,24vw,17.5rem)_minmax(0,1fr)] lg:items-start lg:px-3 lg:py-5">
        <aside className="glass-header sidebar-panel z-20 hidden min-h-0 lg:sticky lg:top-18 lg:flex lg:h-[calc(100dvh-5rem)] lg:flex-col lg:overflow-hidden">
          <AppNavbar user={auth.user} />
        </aside>

        <main
          id="main-content"
          tabIndex={-1}
          className="content-panel relative z-10 min-w-0 py-2 lg:py-4"
        >
          <Outlet />
        </main>
      </div>

      <Dialog
        open={isCommandPaletteOpen}
        onOpenChange={(open) => {
          setIsCommandPaletteOpen(open)
          if (!open) {
            setCommandQuery("")
          }
        }}
      >
        <DialogContent
          className="max-w-[min(42rem,calc(100%-2rem))] p-0"
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Command Palette</DialogTitle>
            <DialogDescription>Quickly jump to a page.</DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-border/70 bg-card/85 backdrop-blur">
            <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2.5">
              <Search className="size-4 text-muted-foreground" />
              <Input
                autoFocus
                value={commandQuery}
                onChange={(event) => setCommandQuery(event.target.value)}
                placeholder="Search pages..."
                aria-label="Search pages"
                className="h-8 border-0 bg-transparent px-1 shadow-none"
              />
              <div className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-background/65 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                <Command className="size-3" />K
              </div>
            </div>

            <div className="max-h-[55vh] overflow-y-auto p-2">
              {filteredCommandItems.length === 0 ? (
                <p className="px-2 py-4 text-sm text-muted-foreground">
                  No matching pages.
                </p>
              ) : (
                <div className="grid gap-1">
                  {filteredCommandItems.map((item) => (
                    <Button
                      key={item.to}
                      unstyled
                      type="button"
                      onClick={() => {
                        setIsCommandPaletteOpen(false)
                        setCommandQuery("")
                        void navigate({ to: item.to })
                      }}
                      className="w-full rounded-lg border border-transparent px-3 py-2 text-left transition-colors duration-75 hover:border-border/60 hover:bg-background/70"
                    >
                      <div className="truncate text-sm font-medium text-foreground">
                        {item.label}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {item.detail}
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
